import { useEffect, useState } from "react";
import { Share, Plus, X } from "lucide-react";

const DISMISS_KEY = "cv_ios_install_hint_dismissed:v1";
// Grace period before we surface the hint on a fresh visit — the viewer
// probably wants to see the app first, not a full-screen ask.
const SHOW_DELAY_MS = 4000;

function isIosSafari() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  // "iPad" was removed from iPadOS 13+ UA; MacIntel + touch is the tell.
  const iOS =
    /iPhone|iPad|iPod/i.test(ua) ||
    (ua.includes("Macintosh") && "ontouchend" in document);
  if (!iOS) return false;
  // Exclude in-app browsers (Instagram, FB, TikTok, Chrome iOS) — they
  // can't Add-to-Home-Screen, so surfacing the hint would just confuse.
  if (/CriOS|FxiOS|EdgiOS|OPiOS|GSA|FBAN|FBAV|Instagram|Line|TikTok/i.test(ua)) return false;
  // Already installed to the home screen? Skip.
  if (window.navigator.standalone === true) return false;
  return true;
}

/**
 * One-time bottom-sheet shown ONLY to iOS Safari visitors, explaining
 * how to Add-to-Home-Screen (since iOS never fires
 * ``beforeinstallprompt``). Persists dismissal in localStorage.
 */
export default function IosInstallHint() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Force-show path: another entry point (e.g. the mobile-app section
    // "Install Cinevoya" button) can dispatch ``cv:show-install-hint``
    // to surface the hint even if the viewer dismissed it earlier.
    const onForceShow = (e) => {
      if (e?.detail?.platform && e.detail.platform !== "ios-safari") return;
      setVisible(true);
    };
    window.addEventListener("cv:show-install-hint", onForceShow);

    if (!isIosSafari()) return () => window.removeEventListener("cv:show-install-hint", onForceShow);
    try {
      if (window.localStorage.getItem(DISMISS_KEY)) {
        return () => window.removeEventListener("cv:show-install-hint", onForceShow);
      }
    } catch { /* localStorage blocked → still show once, don't crash */ }
    const t = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    return () => {
      clearTimeout(t);
      window.removeEventListener("cv:show-install-hint", onForceShow);
    };
  }, []);

  const dismiss = () => {
    setVisible(false);
    try { window.localStorage.setItem(DISMISS_KEY, "1"); } catch { /* noop */ }
  };

  if (!visible) return null;

  return (
    <>
      {/* Dimmed backdrop — click to dismiss (mirrors iOS action-sheet UX). */}
      <div
        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm animate-in fade-in"
        onClick={dismiss}
        aria-hidden="true"
      />
      <div
        className="fixed left-2 right-2 bottom-2 z-50 rounded-2xl bg-surface border border-white/10 shadow-2xl shadow-black/60 p-5 animate-in slide-in-from-bottom-5"
        role="dialog"
        aria-label="Install Cinevoya on iOS"
        data-testid="ios-install-hint"
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Close install hint"
          className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-white/5"
          data-testid="ios-install-dismiss"
        >
          <X className="w-4 h-4" />
        </button>

        <p className="text-xs uppercase tracking-[0.25em] text-terracotta font-semibold mb-2">Add to Home Screen</p>
        <h3 className="font-display text-lg font-bold mb-3">Install Cinevoya on your iPhone</h3>
        <ol className="space-y-3 text-sm text-foreground/80 leading-relaxed">
          <li className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-terracotta/15 border border-terracotta/40 flex items-center justify-center shrink-0">
              <Share className="w-3.5 h-3.5 text-terracotta" />
            </span>
            <span>Tap the <strong className="text-foreground/95">Share</strong> icon in Safari&apos;s toolbar</span>
          </li>
          <li className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-terracotta/15 border border-terracotta/40 flex items-center justify-center shrink-0">
              <Plus className="w-3.5 h-3.5 text-terracotta" />
            </span>
            <span>Choose <strong className="text-foreground/95">Add to Home Screen</strong></span>
          </li>
          <li className="flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0 text-gold text-xs font-bold">
              3
            </span>
            <span>Tap <strong className="text-foreground/95">Add</strong> — you&apos;ll get a full-screen Cinevoya app.</span>
          </li>
        </ol>
      </div>
    </>
  );
}
